import { Link, useNavigate } from '@tanstack/react-router'
import { Check } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { useSession } from '@/lib/auth-client'
import {
  formatMicroUsd,
  formatMicroUsdWithCurrency,
  type PricingPlan,
  planPerGbMicroUsd,
} from '@/lib/billing/billing'
import { cn } from '@/lib/utils'

/** 2000 GB reads better as 2 TB on a plan card. */
export function formatCapGb(gb: number): string {
  return gb >= 1000 ? `${gb / 1000} TB` : `${gb} GB`
}

/**
 * Every plan gets these — one list so home and pricing never drift. Product
 * capabilities (sessions, city targeting, protocols) are deliberately absent:
 * they're on every plan, so they belong to the product story, not the plan
 * comparison. 24/7 support stays so the cheapest plan visibly includes it.
 */
const PLAN_FEATURES = [
  'Included data resets each month',
  'Live per-credential usage stats',
  '24/7 support',
]

/**
 * The plan comparison grid, shared by the home and pricing pages. `plans` is
 * the active catalog from `getPublicPlans` (route loader); per-GB rates are
 * derived from monthly price ÷ included GB so they can never contradict it.
 */
export function PlanGrid({
  plans,
  className,
}: {
  plans: PricingPlan[]
  className?: string
}) {
  // Logged-in visitors skip signup: /billing?subscribe=<slug> auto-starts that
  // plan's checkout for their active org (and just points at the plan card if
  // they already have one). Signed-out visitors carry the plan through signup.
  //
  // The DESTINATION is resolved on click, not in the markup: `useSession`
  // resolves during SSR but is still pending on the client's first render, so
  // branching the `href` here rendered /billing on the server and /signup on
  // the client — a hydration mismatch on every plan card. The rendered link is
  // always the signed-out one (right for the anonymous visitors this page is
  // for, and the href crawlers see); a signed-in click is redirected below.
  const { data: session, isPending } = useSession()
  const authed = !isPending && !!session
  const navigate = useNavigate()

  return (
    <div className={cn('grid gap-4 md:grid-cols-2 xl:grid-cols-4', className)}>
      {plans.map((plan) => {
        const perGb = planPerGbMicroUsd(plan)
        return (
          <Card
            key={plan.slug}
            className={cn(plan.highlighted && 'ring-2 ring-primary')}
          >
            <CardContent className="flex h-full flex-col gap-4 p-6">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-lg font-semibold">{plan.name}</h3>
                {plan.highlighted ? <Badge>Most popular</Badge> : null}
              </div>

              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Check className="size-4 text-primary" />
                {plan.includedGb} GB included / month
              </p>

              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold tracking-tight tabular-nums">
                  {formatMicroUsd(perGb)}
                </span>
                <span className="text-muted-foreground">USD/GB</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {formatMicroUsdWithCurrency(plan.priceMonthlyMicroUsd)} billed
                monthly
              </p>

              <Button
                asChild
                variant={plan.highlighted ? 'default' : 'outline'}
              >
                <Link
                  to="/signup"
                  search={{ plan: plan.slug }}
                  onClick={(e) => {
                    if (!authed) return
                    e.preventDefault()
                    navigate({
                      to: '/billing',
                      search: { subscribe: plan.slug },
                    })
                  }}
                >
                  Buy now
                </Link>
              </Button>

              <Separator />

              <ul className="flex flex-col gap-2 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                  <span className="text-pretty">
                    Add data at {formatMicroUsdWithCurrency(perGb)}/GB, up to{' '}
                    {formatCapGb(plan.topUpCapGb)}/mo
                  </span>
                </li>
                {PLAN_FEATURES.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                    <span className="text-pretty">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
